"use client";

import { PaymentFormSkeleton } from "@offstreetparking/portal-flow/components/CreatePermitWizard/components/PaymentForm";
import type { FlowPaymentGatewayPanelProps } from "@offstreetparking/portal-flow/host";
import dynamic from "next/dynamic";
import { AuthorizeNetForm } from "./components/AuthorizeNetForm";
import { ElavonLightboxForm } from "./components/ElavonLightboxForm";
import { SpreedlyForm } from "./components/SpreedlyForm";
import { TouchNetForm } from "./components/TouchNetForm";
import { useStripe } from "./hooks/useStripe";

const StripeElementsForm = dynamic(() => import("./components/StripeElementsForm").then((m) => m.StripeElementsForm), {
	ssr: false,
	loading: () => <PaymentFormSkeleton />,
});

export const PaymentGatewayPanel = ({
	paymentSession,
	formId,
	locationLookupId,
	onSuccess,
	onError,
	setIsProcessing,
}: FlowPaymentGatewayPanelProps) => {
	const platform = paymentSession?.session?.client?.platform;
	const isStripe = platform === "Stripe";
	const { stripePromise, appearance } = useStripe({
		paymentSession: isStripe ? paymentSession?.session : undefined,
	});

	if (paymentSession?.session && platform === "Elavon") {
		return (
			<ElavonLightboxForm
				paymentSession={paymentSession.session}
				formId={formId}
				onSuccess={onSuccess}
				onError={onError}
				setIsProcessing={setIsProcessing}
			/>
		);
	}
	if (paymentSession?.session && platform === "AuthorizeNet") {
		return (
			<AuthorizeNetForm
				paymentSession={paymentSession.session}
				formId={formId}
				onSuccess={onSuccess}
				onError={onError}
				setIsProcessing={setIsProcessing}
			/>
		);
	}
	if (paymentSession?.session && platform === "Spreedly") {
		return (
			<SpreedlyForm
				paymentSession={paymentSession.session}
				formId={formId}
				onSuccess={onSuccess}
				onError={onError}
				setIsProcessing={setIsProcessing}
			/>
		);
	}
	if (paymentSession?.session && platform === "TouchNet") {
		return (
			<TouchNetForm
				paymentSession={paymentSession.session}
				formId={formId}
				setIsProcessing={setIsProcessing}
				pendingSavedPermit={
					locationLookupId &&
					paymentSession.permit?.publicIdentifier &&
					paymentSession.permit.publicAccessToken
						? {
								locationLookupId,
								publicIdentifier: paymentSession.permit.publicIdentifier,
								publicAccessToken: paymentSession.permit.publicAccessToken,
							}
						: undefined
				}
			/>
		);
	}
	if (paymentSession?.session && stripePromise) {
		return (
			<StripeElementsForm
				stripePromise={stripePromise}
				clientSecret={paymentSession.session.clientSecret}
				appearance={appearance}
				formId={formId}
				paymentId={paymentSession.session.paymentId}
				onSuccess={onSuccess}
				onError={onError}
				setIsProcessing={setIsProcessing}
			/>
		);
	}
	return stripePromise ? <PaymentFormSkeleton /> : null;
};
